<template>
  <v-container class="contact-page" fluid>
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Insurance Partners <span class="thai">บริษัทประกันคู่สัญญา</span></h1>
        <p class="subhead mb-0">Manage contracted insurers and which hospital branches display each partner.</p>
      </div>
      <v-btn v-if="canAdd" class="setup-btn" color="primary" @click="$router.push({name: 'insurance-partner-create'})">
        <v-icon left small>mdi-plus</v-icon>
        Create Partner
      </v-btn>
    </div>

    <v-card class="table-card">
      <div class="category-bar" v-if="insuranceTypeOptions.length > 1">
        <button
          v-for="type in insuranceTypeOptions"
          :key="type.value"
          type="button"
          class="category-chip"
          :class="{'active': selectedInsuranceType === type.value}"
          @click="onTypeFilterChange(type.value)"
        >
          {{ type.text }}
        </button>
      </div>

      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field
            v-model="searchText"
            append-icon="mdi-magnify"
            outlined
            dense
            hide-details
            label="Search by company name..."
            clearable
            @input="onSearch"
          />
        </div>
        <v-select v-if="isSuperAdmin"
          v-model="selectedOrg"
          :items="orgOptionsWithAll"
          outlined
          dense
          hide-details
          item-text="text"
          item-value="value"
          class="small-select"
          :disabled="isBranchLocked"
          placeholder="All Branches"
          @input="onOrgFilterChange"
        />
        <v-btn outlined class="filter-btn" :class="{'has-filtered': hasAppliedFilters}" @click="handleFilterButtonClick">
          <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
          {{ hasAppliedFilters ? 'Clear' : 'Filter' }}
        </v-btn>
      </div>

      <v-data-table
        :loading="isPageLoading"
        :headers="headers"
        :items="list"
        :items-per-page="10"
        hide-default-footer
        class="contact-table"
      >
        <template v-slot:item.no="{index, item}">{{ rowNo(index, item) }}</template>

        <template v-slot:item.company_name="{item}">
          <strong>{{ item.name_en || item.name_th || '-' }}</strong>
        </template>

        <template v-slot:item.insurance_type="{item}">
          <span class="type-pill">{{ getInsuranceTypeLabel(item.type) }}</span>
        </template>

        <template v-slot:item.eligible_branches="{item}">
          {{ formatEligibleBranches(item) }}
        </template>

        <template v-slot:item.action="{item}">
          <button v-if="canEdit" class="icon-btn" @click.stop="goToEdit(item)"><v-icon size="16">mdi-pencil-outline</v-icon></button>
          <button v-if="canDelete" class="icon-btn ml-2" @click.stop="remove(item)"><v-icon size="16">mdi-delete-outline</v-icon></button>
        </template>
      </v-data-table>

      <div class="table-foot" v-if="list.length != 0">
        Showing 1 to {{ list.length }} of {{ list.length }} entries
      </div>
    </v-card>

  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'

export default {
  data() {
    return {
      searchText: '',
      selectedOrg: '',
      selectedInsuranceType: '',
      localLoading: false,
      headers: [
        {text: 'NO.', value: 'no', sortable: false, width: 70},
        {text: 'COMPANY NAME', value: 'company_name', sortable: false},
        {text: 'INSURANCE TYPE', value: 'insurance_type', sortable: false, width: 240},
        {text: 'ELIGIBLE BRANCHES', value: 'eligible_branches', sortable: false, width: 240},
        {text: 'ACTIONS', value: 'action', sortable: false, width: 120, align: 'center'}
      ]
    }
  },
  computed: {
    ...mapState('insurancePartners', ['list', 'isLoading', 'orgOptions']),
    ...mapState('auth', ['user']),
    canAdd() { return hasPermision('CanAddInsurancePartner') },
    canEdit() { return hasPermision('CanEditInsurancePartner') },
    canDelete() { return hasPermision('CanDeleteInsurancePartner') },
    isBranchLocked() {
      return !this.isSuperAdmin || this.orgOptions.length === 1
    },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    orgOptionsWithAll() {
      return [{text: 'All Branches', value: ''}, ...this.orgOptions]
    },
    insuranceTypeOptions() {
      return [
        {text: 'All', value: ''},
        {text: 'Life Insurance', value: 'life'},
        {text: 'Health Insurance', value: 'health'},
        {text: 'Broker', value: 'broker'}
      ]
    },
    hasAppliedFilters() {
      const hasSearch = !!(this.searchText || '').trim()
      const hasTypeFilter = !!this.selectedInsuranceType
      const hasBranchFilter = this.isSuperAdmin && !!(this.selectedOrg || '').trim()
      return hasSearch || hasTypeFilter || hasBranchFilter
    },
    isPageLoading() {
      return this.localLoading || this.isLoading
    }
  },
  async created() {
    this.localLoading = true
    try {
      this.setResource('insurance-partners')
      await this.fetchOrgOptions()
      this.setTextSearch('')
      this.setFilterCategory(this.selectedInsuranceType || '')
      if (!this.isSuperAdmin) {
        this.selectedOrg = this.getUserOrgCode()
        this.setFilterOrgCode(this.selectedOrg)
      } else if (this.orgOptions.length === 1) {
        this.selectedOrg = this.orgOptions[0].value
        this.setFilterOrgCode(this.selectedOrg)
      } else {
        this.setFilterOrgCode('')
      }
      await this.fetch()
    } finally {
      this.localLoading = false
    }
  },
  methods: {
    ...mapActions('insurancePartners', ['setResource', 'fetch', 'setTextSearch', 'setFilterOrgCode', 'setFilterCategory', 'fetchOrgOptions', 'deleteById']),
    getInsuranceTypeLabel(type) {
      if (type === 'health') return 'Health Insurance'
      if (type === 'broker') return 'Broker'
      return 'Life Insurance'
    },
    async onTypeFilterChange(type) {
      this.selectedInsuranceType = type
      this.localLoading = true
      try {
        this.setFilterCategory(this.selectedInsuranceType || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    rowNo(index, item) {
      let rowIndex = Number(index)
      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = (this.list || []).findIndex((listItem) => {
          if (item && item.id && listItem.id) return String(listItem.id) === String(item.id)
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }
      return rowIndex + 1
    },
    getUserOrgCode() {
      const user = this.user || {}
      return user.org_code || user.org_codename || user.org || ''
    },
    async onSearch() {
      this.localLoading = true
      try {
        this.setTextSearch(this.searchText || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onOrgFilterChange() {
      this.localLoading = true
      try {
        this.setFilterOrgCode(this.selectedOrg || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async handleFilterButtonClick() {
      if (!this.hasAppliedFilters) return

      this.localLoading = true
      try {
        this.searchText = ''
        this.selectedInsuranceType = ''
        this.selectedOrg = this.isSuperAdmin ? '' : this.getUserOrgCode()
        this.setTextSearch('')
        this.setFilterOrgCode(this.selectedOrg)
        this.setFilterCategory('')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    formatEligibleBranches(item) {
      const selected = Array.isArray(item.org_codenames) ? item.org_codenames.length : 0
      const total = this.orgOptions.length || 0
      return `${selected} of ${total} branches`
    },
    goToEdit(item) {
      this.$router.push({name: 'insurance-partner-edit', params: {id: item.id}})
    },
    async remove(item) {
      if (!window.confirm('Delete this record?')) return
      this.localLoading = true
      try {
        const ok = await this.deleteById(item.id)
        if (ok) await this.fetch()
      } finally {
        this.localLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.thai { color: #7c8aa5; font-size: 18px; margin-left: 6px; }
.subhead { color: #6d7f9e; font-size: 14px; }
.setup-btn { text-transform: none; font-weight: 700; border-radius: 10px; }
.table-card { border: 1px solid #e4ebf7; border-radius: 12px; box-shadow: 0 1px 8px rgba(16,35,72,.05); }
.category-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; flex-wrap: wrap; }
.category-chip { border: 1px solid #dfe7f6; background: #fff; color: #5f7397; border-radius: 999px; padding: 8px 18px; font-size: 14px; font-weight: 700; cursor: pointer; }
.category-chip.active { background: #eef4ff; border-color: #bcd3ff; color: #1e49ba; }
.filter-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; }
.search-wrap { flex: 5; }
.small-select { flex: 1.5; }
.filter-btn { text-transform: none; font-weight: 600; min-width: 96px; }
.filter-btn.has-filtered { border-color: #f3b5b5 !important; color: #c62828 !important; background: #fdecec; }
.type-pill { background: #eef4ff; color: #1e49ba; border-radius: 8px; padding: 4px 12px; font-weight: 700; font-size: 14px; }
.icon-btn { border: 1px solid #dce6f8; border-radius: 8px; width: 36px; height: 36px; background: #fff; }
.table-foot { padding: 12px 14px; border-top: 1px solid #edf2fb; color: #7c8aa5; font-size: 13px; }
.contact-table::v-deep .v-data-table-header th { color: #7c8aa5 !important; font-size: 12px; font-weight: 700; }
</style>
